import styled from "styled-components";
import { useEffect, useState, useRef } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { useNavigate } from "react-router-dom";
import validator from "validator";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import AutoresponderMessages from "./AutoresponderMessages";
import AutoresponderMessageEditorContainer from "./AutoresponderMessageEditorContainer";
import Backdrop from "components/other/Backdrop";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ModalWindow from "../other/ModalWindow";
import ButtonWhite from "../buttons/ButtonWhite";
import ButtonPrimary from "../buttons/ButtonPrimary";
import AutoresponderMessagePreviewContainer from "./AutoresponderMessagePreviewContainer";
import ButtonPrimaryIcon from "components/buttons/ButtonPrimaryIcon";

import crossIcon from "assets/img/icons/icons8-cross.svg";
import warningRedIcon from "assets/img/icons/warning-icon-red.svg";
import plusIcon from "assets/img/icons/plus-sign.svg";
import plusWhiteIcon from "assets/img/icons/plus-sign-white.svg";
import EmailTemplatesContainer from "./EmailTemplatesContainer";

const AutoresponderMessagesContainer = styled.div`
  position: relative;

  margin-top: ${css.VERTICAL_BOX_MARGIN_MLARGE};
`;

const TableButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const ModalWrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const EditorModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 100%;
  max-width: 1600px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 2000;
`;

const EditorModal = styled.div`
  position: relative;
  margin-top: 30px;
  width: 100%;
  padding: 25px 0px;
  box-sizing: border-box;
  height: calc(100vh - 120px);
  transform: translateY(-25px);

  overflow-y: auto;

  border-radius: ${css.BOX_RADIUS_PRIMARY};
  background: ${css.BLUE_BOX_GRADIENT};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (min-width: ${globalConstants.switchMobile}) {
    margin-top: 0px;
  }
`;

const EditorHeader = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0 20px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (min-width: ${globalConstants.switchMobile}) {
    margin-top: 0px;
    padding: 0 25px;
  }
`;

const EditorTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const CloseBox = styled.div`
  align-self: flex-start;
`;

const FormModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  color: #fff;

  width: 100%;
  max-width: 520px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 3000;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const LeaveModal = styled.div`
  margin-top: 50px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const LeaveModalButtons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const LeaveModalInfo = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_SECONDARY};
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  gap: 12px;
`;

const LeaveModalInfoIcon = styled.div`
  padding-top: 3px;

  width: 22px;
  min-width: 22px;
  height: 22px;
`;

const LeaveModalInfoText = styled.div``;

const ButtonWrap = styled.div`
  display: flex;
`;

const ConfirmDelete = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ConfirmDeleteWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmDeleteText = styled.div`
  width: 100%;
  line-height: 130%;
`;

const ConfirmDeleteSubject = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};
  width: 100%;

  font-weight: 500;
  line-height: 130%;
`;

const ConfirmDeleteButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const TemplatesModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  color: #fff;

  width: 100%;
  max-width: 800px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 4000;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const TemplatesModal = styled.div`
  margin-top: 50px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

export default ({ autoresponderId }) => {
  const navigate = useNavigate();

  const emailEditorRef = useRef();

  const [editorOpened, setEditorOpened] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [leaveModal, setLeaveModal] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [previewingMessageId, setPreviewingMessageId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteMessageSubject, setConfirmDeleteMessageSubject] =
    useState(null);
  const [loadingDeleteMessage, setLoadingDeleteMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmDuplicateId, setConfirmDuplicateId] = useState(null);
  const [loadingDuplicateMessage, setLoadingDuplicateMessage] = useState(false);
  const [editOrderActive, setEditOrderActive] = useState(false);
  const [orderWarnings, setOrderWarnings] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [loadingUpdateOrder, setLoadingUpdateOrder] = useState(false);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
  const [templatesModal, setTemplatesModal] = useState(false);

  // Fetch data
  const GET_RESPONDER_MESSAGES = gql`
    query GetResponderMessages($responderId: ID!) {
      responderMessages(responderId: $responderId) {
        id
        order
        status
        subject
        sent_emails_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  `;

  const { data, error, refetch } = useQuery(GET_RESPONDER_MESSAGES, {
    variables: {
      responderId: autoresponderId,
    },
    skip: !autoresponderId,
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching responder messages data", error);
    captureException(error);
  }

  const openPreview = (id) => {
    navigate(`/`);
  };

  const onEditClick = (id) => {
    setEditingMessageId(id);
    setEditorOpened(true);
  };

  // handle Editor modal
  const closeEditorAttempt = () => {
    setLeaveModal(true);
  };

  const closeEditor = () => {
    setEditingMessageId(null);
    setEditorOpened(false);
    setLeaveModal(false);

    refetch();
  };

  const closeLeaveModal = () => {
    setLeaveModal(false);
  };

  // handle Preview modal
  const onPreviewClick = (id) => {
    setPreviewOpened(true);
    setPreviewingMessageId(id);
  };

  const closePreview = () => {
    setPreviewOpened(false);
    setPreviewingMessageId(null);
  };

  // handle Create new message
  const onCreateMessageClick = () => {
    setEditorOpened(true);
    setEditingMessageId(null);
  };

  // handle Delete message
  const onDeleteClick = (id) => {
    setConfirmDeleteId(id);
  };

  useEffect(() => {
    if (confirmDeleteId && data) {
      const messageToDelete = data.responderMessages.find(
        (message) => message.id === confirmDeleteId
      );

      if (messageToDelete)
        setConfirmDeleteMessageSubject(messageToDelete.subject);
    }
  }, [confirmDeleteId]);

  const closeDeleteModal = () => {
    setConfirmDeleteId(null);
    setConfirmDeleteMessageSubject(null);
  };

  const DELETE_AUTORESPONDER_MESSAGE = gql`
    mutation DeleteAutoresponderMessage($messageId: ID!) {
      deleteAutoresponderMessage(messageId: $messageId) {
        resultCode
      }
    }
  `;

  const [deleteAutoresponderMessage] = useMutation(
    DELETE_AUTORESPONDER_MESSAGE
  );

  const deleteMessage = async () => {
    try {
      if (loadingDeleteMessage) return;
      if (!confirmDeleteId) return;

      setLoadingDeleteMessage(true);

      const result = await deleteAutoresponderMessage({
        variables: {
          messageId: confirmDeleteId,
        },
      });

      const { resultCode } = result.data.deleteAutoresponderMessage;

      setLoadingDeleteMessage(false);

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        return;
      }

      setConfirmDeleteId(null);
      setConfirmDeleteMessageSubject(null);

      refetch();
    } catch (error) {
      console.log("deleteMessage error", error);
      captureException(error);
      setLoadingDeleteMessage(false);
      return;
    }
  };

  // handle Duplicate message
  const onDuplicateClick = (id) => {
    setConfirmDuplicateId(id);
  };

  const closeDuplicateModal = () => {
    setConfirmDuplicateId(null);
  };

  const DUPLICATE_AUTORESPONDER_MESSAGE = gql`
    mutation DuplicateAutoresponderMessage($messageId: ID!) {
      duplicateAutoresponderMessage(messageId: $messageId) {
        resultCode
      }
    }
  `;

  const [duplicateAutoresponderMessage] = useMutation(
    DUPLICATE_AUTORESPONDER_MESSAGE
  );

  const duplicateMessage = async () => {
    try {
      if (loadingDuplicateMessage) return;
      if (!confirmDuplicateId) return;

      setLoadingDuplicateMessage(true);

      const result = await duplicateAutoresponderMessage({
        variables: {
          messageId: confirmDuplicateId,
        },
      });

      const { resultCode } = result.data.duplicateAutoresponderMessage;

      setLoadingDuplicateMessage(false);

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        return;
      }

      setConfirmDuplicateId(null);
      refetch();
    } catch (error) {
      console.log("duplicateMessage error", error);
      captureException(error);
      setLoadingDuplicateMessage(false);
      return;
    }
  };

  // handle Edit schedule
  const onEditOrderClick = () => {
    setEditOrderActive(true);
  };

  useEffect(() => {
    if (data) {
      let newCalendar = {};

      for (const message of data.responderMessages) {
        newCalendar[message.id] = message.order;
      }

      setSchedule(newCalendar);
    }
  }, [data]);

  const onOrderInputChange = (id, e) => {
    let newCalendar = { ...schedule };

    console.log("e.target.value", id, e.target.value);

    const value = validator.whitelist(e.target.value, "0123456789");

    newCalendar[id] = Number(value);

    console.log("newCalendar", newCalendar);

    setSchedule(newCalendar);
  };

  function findDuplicateValues(obj) {
    const valueToUuids = {};
    for (const [uuid, value] of Object.entries(obj)) {
      if (!valueToUuids[value]) {
        valueToUuids[value] = [];
      }
      valueToUuids[value].push(uuid);
    }

    const duplicates = [];
    for (const uuids of Object.values(valueToUuids)) {
      if (uuids.length > 1) {
        duplicates.push(...uuids);
      }
    }

    return duplicates;
  }

  const UPDATE_AUTORESPONDER_MESSAGE_ORDER = gql`
    mutation UpdateAutoresponderMessageOrder(
      $responderId: ID!
      $schedule: JSON!
    ) {
      updateAutoresponderMessageOrder(
        responderId: $responderId
        schedule: $schedule
      ) {
        resultCode
      }
    }
  `;

  const [updateAutoresponderMessageOrder] = useMutation(
    UPDATE_AUTORESPONDER_MESSAGE_ORDER
  );

  const updateSchedule = async () => {
    try {
      if (loadingUpdateOrder) return;

      setLoadingUpdateOrder(true);

      const duplicates = findDuplicateValues(schedule);
      console.log("duplicates", duplicates);

      if (duplicates.length > 0) {
        setOrderWarnings(duplicates);
        setLoadingUpdateOrder(false);
        return;
      }

      const result = await updateAutoresponderMessageOrder({
        variables: {
          responderId: autoresponderId,
          schedule,
        },
      });

      const { resultCode } = result.data.updateAutoresponderMessageOrder;
      console.log({ resultCode });

      setLoadingUpdateOrder(false);

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        return;
      }

      setEditOrderActive(false);
      refetch();
    } catch (error) {
      console.log("updateSchedule error", error);
      captureException(error);
      setLoadingUpdateOrder(false);
      return;
    }
  };

  // handle Status change
  const CHANGE_AUTORESPONDER_MESSAGE_STATUS = gql`
    mutation ChangeAutoresponderMessageStatus($messageId: ID!) {
      changeAutoresponderMessageStatus(messageId: $messageId) {
        id
        status
      }
    }
  `;

  const [changeAutoresponderMessageStatus] = useMutation(
    CHANGE_AUTORESPONDER_MESSAGE_STATUS
  );

  const onStatusToggleClick = async (id) => {
    try {
      if (loadingChangeStatus) return;

      setLoadingChangeStatus(true);

      const result = await changeAutoresponderMessageStatus({
        variables: {
          messageId: id,
        },
      });

      const { resultCode } = result.data.changeAutoresponderMessageStatus;
      console.log({ resultCode });

      setLoadingChangeStatus(false);

      if (resultCode !== 0) {
        setErrorMessage(
          "An error occurred while submitting the request. Please try again or contact support."
        );

        return;
      }

      //refetch();
    } catch (error) {
      console.log("onStatusToggleClick error", error);
      captureException(error);
      setLoadingChangeStatus(false);
      return;
    }
  };

  // handle Templates
  const openTemplatesModal = () => {
    setTemplatesModal(true);
  };

  const closeTemplatesModal = () => {
    setTemplatesModal(false);
  };

  const loadTemplate = (design) => {
    console.log("Loading template", design);
    const unlayer = emailEditorRef.current?.editor;

    unlayer.loadDesign(design);
    setTemplatesModal(false);
  };

  //console.log("data", data);

  return (
    <AutoresponderMessagesContainer>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="300px" />
        </LoadingBox>
      )}
      {data && (
        <TableButtonWrap>
          <AutoresponderMessages
            data={data}
            openPreview={openPreview}
            onEditClick={onEditClick}
            onPreviewClick={onPreviewClick}
            onDeleteClick={onDeleteClick}
            onDuplicateClick={onDuplicateClick}
            onEditOrderClick={onEditOrderClick}
            editOrderActive={editOrderActive}
            orderWarnings={orderWarnings}
            schedule={schedule}
            onOrderInputChange={onOrderInputChange}
            updateSchedule={updateSchedule}
            onStatusToggleClick={onStatusToggleClick}
          />
          <ButtonWrap>
            <ButtonPrimaryIcon
              icon={plusIcon}
              iconHover={plusWhiteIcon}
              onClick={onCreateMessageClick}
            >
              Create Message
            </ButtonPrimaryIcon>
          </ButtonWrap>
        </TableButtonWrap>
      )}
      {editorOpened && (
        <ModalWrap>
          <Backdrop
            onClick={closeEditorAttempt}
            background="rgba(0, 0, 0, 0.7)"
          />
          <EditorModalWrap>
            <EditorModal>
              <EditorHeader>
                <EditorTitle>
                  {editingMessageId ? "Edit Message" : "Create New Message"}
                </EditorTitle>
                <CloseBox>
                  <RoundActionIcon
                    icon={crossIcon}
                    iconAlt="Close"
                    onClick={closeEditorAttempt}
                  />
                </CloseBox>
              </EditorHeader>
              <AutoresponderMessageEditorContainer
                messageId={editingMessageId}
                autoresponderId={autoresponderId}
                closeEditor={closeEditor}
                openTemplatesModal={openTemplatesModal}
                emailEditorRef={emailEditorRef}
              />
            </EditorModal>
          </EditorModalWrap>
        </ModalWrap>
      )}
      {templatesModal && (
        <>
          <Backdrop
            onClick={closeTemplatesModal}
            background="rgba(0, 0, 0, 0.7)"
            zIndex="2500"
          />
          <TemplatesModalWrap>
            <ModalWindow
              modal="emailTemplates"
              handleModalClose={closeTemplatesModal}
              fullScreenHeight={true}
              title="Email Templates"
              maxHeight="calc(100vh - 160px)"
            >
              <TemplatesModal>
                <EmailTemplatesContainer loadTemplate={loadTemplate} />
              </TemplatesModal>
            </ModalWindow>
          </TemplatesModalWrap>
        </>
      )}
      {leaveModal && (
        <>
          <Backdrop
            onClick={closeLeaveModal}
            background="rgba(0, 0, 0, 0.7)"
            zIndex="2500"
          />
          <FormModalWrap>
            <ModalWindow
              handleModalClose={closeLeaveModal}
              fullScreenHeight={true}
              title="Leave Message Editor"
            >
              <LeaveModal>
                <LeaveModalInfo>
                  <LeaveModalInfoIcon>
                    <img src={warningRedIcon} style={{ width: "100%" }} />
                  </LeaveModalInfoIcon>
                  <LeaveModalInfoText>
                    You are about to leave the editor. If you have any unsaved
                    changes, they will be lost.
                  </LeaveModalInfoText>
                </LeaveModalInfo>
                <LeaveModalButtons>
                  <ButtonWhite onClick={closeLeaveModal}>Stay</ButtonWhite>
                  <ButtonPrimary onClick={closeEditor}>Leave</ButtonPrimary>
                </LeaveModalButtons>
              </LeaveModal>
            </ModalWindow>
          </FormModalWrap>
        </>
      )}
      {previewOpened && (
        <ModalWrap>
          <Backdrop onClick={closePreview} background="rgba(0, 0, 0, 0.7)" />
          <AutoresponderMessagePreviewContainer
            messageId={previewingMessageId}
            closePreview={closePreview}
          />
        </ModalWrap>
      )}
      {confirmDeleteId && (
        <ModalWrap>
          <Backdrop
            onClick={closeDeleteModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <ConfirmDelete>
            <ModalWindow
              title="Delete Message"
              handleModalClose={closeDeleteModal}
              fullScreenHeight={true}
            >
              <ConfirmDeleteWrap>
                <ConfirmDeleteText>
                  Do you really want to delete the following message?
                </ConfirmDeleteText>
                <ConfirmDeleteSubject>
                  {confirmDeleteMessageSubject}
                </ConfirmDeleteSubject>
                <ConfirmDeleteButtons>
                  <ButtonWhite onClick={closeDeleteModal}>Cancel</ButtonWhite>
                  <ButtonPrimary onClick={deleteMessage}>Delete</ButtonPrimary>
                </ConfirmDeleteButtons>
              </ConfirmDeleteWrap>
            </ModalWindow>
          </ConfirmDelete>
        </ModalWrap>
      )}
      {confirmDuplicateId && (
        <ModalWrap>
          <Backdrop
            onClick={closeDuplicateModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <ConfirmDelete>
            <ModalWindow
              title="Duplicate Message"
              handleModalClose={closeDuplicateModal}
              fullScreenHeight={true}
            >
              <ConfirmDeleteWrap>
                <ConfirmDeleteText>
                  A copy of the message will be created and added to the end of
                  the list.
                </ConfirmDeleteText>
                <ConfirmDeleteSubject>
                  {confirmDeleteMessageSubject}
                </ConfirmDeleteSubject>
                <ConfirmDeleteButtons>
                  <ButtonWhite onClick={closeDuplicateModal}>
                    Cancel
                  </ButtonWhite>
                  <ButtonPrimary onClick={duplicateMessage}>
                    Proceed
                  </ButtonPrimary>
                </ConfirmDeleteButtons>
              </ConfirmDeleteWrap>
            </ModalWindow>
          </ConfirmDelete>
        </ModalWrap>
      )}
    </AutoresponderMessagesContainer>
  );
};
